<template lang="pug">
layout-main(:showSidebar="false" :hideHeader="hideHeader")
  managementPageWithActions.scrollable(:title="$t('customer_tab.title')"
                classPrefix="customer-info"
                @fetchData="loadList"
                @chooseAction="chooseAction"
                @removePreFilter="removePreFilter"
                @loadAll="loadAll",
                @downloadData="downloadData"
                @stopDataLoading="stopLoadAll",
                @toggleOpen="toggleOpen"
                @loadAndSelectSome="loadAndSelectSome"
                @unselectAll="clearCustomerTableSelection"
                :searchable="!showAdvancedFilter"
                :isDataLoading="isLoadingAll || isLoading",
                :preFilterText="preFilterText"
                :chosenAction="chosenAction"
                :loadWithQuery="$route.query.query"
                :searchPageSize="searchPageSize"
                :showDatePicker="!showAdvancedFilter"
                :datePickerOptions="Object.keys(datePickerOptions)"
                :disabledDatePickerRange="disabledDatePickerRange"
                :customerAgentFilter="!showAdvancedFilter"
                :customerAgentOptions="Object.keys(customerAgentOptions)"
                :customerChannelFilter="showChannelFilter && !showAdvancedFilter"
                :customerChannelOptions="channelOptions"
                :customerSegmentFilter="!showAdvancedFilter"
                :customerSegmentOptions="segments"
                :customerProfileFilter="!!profileSearchableFields.length"
                :customerProfileOptions="profileSearchableFields"
                :actionOptions="aclPermittedActions"
                :showTotals="!showAdvancedFilter"
                :showLoadAll="!showAdvancedFilter"
                :filteredSearch="false"
                :showDownloadButton="false"
                :pagination="pagination"
                :creatable="false"
                :helpText="helpText"
                :actionsErrorText="actionsErrorText"
                :hasRowSelections="hasRowSelections"
                :openFilters="isFilterOpen"
                :searchOnEnterOnly="true"
                :hasSelectAndLoad="showAdvancedFilter"
                :selectedRowsCount="selectedRowsCount"
                ref="managementPage")

    template(v-slot:editor)
      actions-panel(v-if="actionFormShown"
                  v-bind="{...actionCardParams}"
                  @commitAction="commitAction"
                  @closeActionPanel="closeActionPanel")

    template(v-slot:controls)
      customer-table-controls(v-if="showAdvancedFilter"
                            :customerProfileOptions="profileSearchableFields"
                            :customerAgentOptions="Object.keys(advancedCustomerAgentOptions)"
                            :customerChannelOptions="sortedChannels"
                            :customerSegmentOptions="segments"
                            :customerTagOptions="tagsListState"
                            :teamFilterOptions="teams"
                            :agentFilterOptions="sortedAgents"
                            :disabledDatePickerRange="disabledDatePickerRange"
                            :pagination="pagination"
                            @apply-filter="applyAdvancedFilter")


    template(v-slot:body v-if="isMobileContext")
      aiq-table.customers-scrollbar(:data="customers"
                        style="width: 100%"
                        ref="customersTable"
                        row-key="id"
                        :default-sort="defaultSortOrder"
                        v-loading="actionInProgress"
                        @selection-change="onSelect"
                        @select-all="onSelectAll")
        aiq-table-column(type="selection",
                        width="48px",
                        :reserve-selection="true"
                        label-class-name="is-bordered")
        aiq-table-column(type="expand")
          template(v-slot:default="item")
            .expandable-row
              p(v-if="getIsCustomerTableColumnVisible('email')")
                span.col-label {{ getCustomerTableColumnLabel('email') }}:
                span.col-value {{ getCustomerEmail(item.row) }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('joined_date')")
                span.col-label {{ getCustomerTableColumnLabel('joined_date') }}:
                span.col-value {{ getFormattedJoinedDate(item.row) }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('segment')")
                span.col-label {{ getCustomerTableColumnLabel('segment') }}:
                span.col-value {{ getFormattedCustomerSegment(item.row) }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('primary_agent')")
                span.col-label {{ getCustomerTableColumnLabel('primary_agent') }}:
                aiq-button(link type="primary" size="small", @click="canView('/users') && filterToAgent(item.row.primary_agent_details)") {{ getPrimaryAgentName(item.row) }}
              p(v-if="getIsCustomerTableColumnVisible('assigned_agents')")
                span.col-label {{ getCustomerTableColumnLabel('assigned_agents') }}:
                .pills-list
                  aiq-button(v-for="agent in item.row.assigned_agents_details"
                          text
                          size="small",
                          class="agent-button"
                          :key="agent.id"
                          @click="canView('/users') && filterToAgent(agent)")
                    .tag-tooltip(v-if="getAssignedAgentName(agent).length >= maxAssignedAgentNameLength")
                      aiq-tooltip(:content="getAssignedAgentName(agent)")
                        aiq-tag {{ getTruncatedAssignedAgentName(agent) }}
                    aiq-tag(v-else) {{ getAssignedAgentName(agent) }}
              p(v-if="getIsCustomerTableColumnVisible('last_feedback_sent')")
                span.col-label {{ getCustomerTableColumnLabel('last_feedback_sent') }}:
                span.col-value {{ getFormattedFeedbackTime(item.row, 'customer_feedback_details.last_feedback_sent_at') }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('last_feedback_completed')")
                span.col-label {{ getCustomerTableColumnLabel('last_feedback_completed') }}:
                span.col-value {{ getFormattedFeedbackTime(item.row, 'customer_feedback_details.last_feedback_completed_at') }} &nbsp;

        aiq-table-column(:label="getCustomerTableColumnLabel('name')", :sortable="true", :sort-method="onSortName", label-class-name="is-bordered")
          template(v-slot:default="item")
            span {{ $filters.toUpperFirst(getCustomerName(item.row)) }} &nbsp;
        aiq-table-column(v-if="getIsCustomerTableColumnVisible('last_interaction')"
                         prop="last_chat_details.last_chat_time"
                         :label="getCustomerTableColumnLabel('last_interaction')"
                         :sortable="true"
                         :sort-method="onSortByTime('last_chat_details.last_chat_time')"
                         label-class-name="is-bordered")
          template(v-slot:default="item")
            aiq-button.date-button(link type="primary" size="small", @click="goToLastConversation(item.row)") {{ getFormattedLastChatTime(item.row) }}

    template(v-slot:body v-if="!isMobileContext")
      aiq-table.customers-scrollbar(:data="customers"
                      ref="customersTable"
                      row-key="id"
                      :default-sort="defaultSortOrder"
                      v-loading="actionInProgress"
                      @selection-change="onSelect"
                      @select-all="onSelect")
        aiq-table-column(type="selection",
                        width="48px",
                        :reserve-selection="true"
                        label-class-name="is-bordered")
        template(v-for="column in visibleCustomerTableColumns" :key="column.name")
          aiq-table-column(v-if="column.name === 'name'"
                           :label="getCustomerTableColumnLabel('name')"
                           :sortable="true"
                           :sort-method="onSortName"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              tooltip-info(:item="scope.row")
                template(v-slot:customerInfoItem) {{ $filters.toUpperFirst(getCustomerName(scope.row)) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'email'"
                           :label="getCustomerTableColumnLabel('email')"
                           :sortable="true"
                           :sort-method="onSortEmail"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getCustomerEmail(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'phone'"
                           :label="getCustomerTableColumnLabel('phone')"
                           :sortable="true"
                           :sort-method="onSortPhone"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getCustomerPhone(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'external_id'"
                           :label="getCustomerTableColumnLabel('external_id')"
                           :sortable="true"
                           :sort-method="onSortExternalId"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getCustomerExternalId(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'joined_date'"
                           prop="joined_date"
                           :label="getCustomerTableColumnLabel('joined_date')"
                           :sortable="true"
                           :sort-method="onSortJoinedDate"
                           label-class-name="is-bordered"
                           width="120px")
            template(v-slot:default="scope")
              span {{ getFormattedJoinedDate(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'last_interaction'"
                           prop="last_chat_details.last_chat_time"
                           :label="getCustomerTableColumnLabel(showAdvancedFilter ? 'last_interaction' : 'last_chat_time')"
                           :sortable="true"
                           :sort-method="onSortByTime('last_chat_details.last_chat_time')"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              aiq-button(link type="primary" size="small", @click="goToLastConversation(scope.row)") {{ getFormattedLastChatTime(scope.row) }}
          aiq-table-column(v-else-if="column.name === 'segment'"
                           :label="getCustomerTableColumnLabel('segment')"
                           :sortable="true"
                           :sort-method="onSortCustomerSegment"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getFormattedCustomerSegment(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'primary_agent'"
                           :label="getCustomerTableColumnLabel('primary_agent')"
                           :sortable="true"
                           :sort-method="onSortPrimaryAgent"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              aiq-button(link type="primary" size="small", class="agent-button", @click="canView('/users') && filterToAgent(scope.row.primary_agent_details)") {{ getPrimaryAgentName(scope.row) }}
          aiq-table-column(v-else-if="column.name === 'assigned_agents'"
                           :label="getCustomerTableColumnLabel('assigned_agents')"
                           :sortable="false"
                           width="170px"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              .tag-list
                aiq-button(v-for="agent in scope.row.assigned_agents_details"
                         text
                         size="small",
                         class="agent-button"
                         :key="agent.id"
                         @click="canView('/users') && filterToAgent(agent)")
                  .tag-tooltip(v-if="getAssignedAgentName(agent).length >= maxAssignedAgentNameLength")
                    aiq-tooltip(:content="getAssignedAgentName(agent)")
                      aiq-tag {{ getTruncatedAssignedAgentName(agent) }}
                  aiq-tag(v-else) {{ getAssignedAgentName(agent) }}
          aiq-table-column(v-else-if="column.name === 'last_feedback_sent'"
                           :label="getCustomerTableColumnLabel(showAdvancedFilter ? 'last_feedback_sent' : 'last_feedback_sent_at')"
                           :sortable="true"
                           :sort-method="onSortByTime('customer_feedback_details.last_feedback_sent_at')"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getFormattedFeedbackTime(scope.row, 'customer_feedback_details.last_feedback_sent_at') }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'last_feedback_completed'"
                           :label="getCustomerTableColumnLabel(showAdvancedFilter ? 'last_feedback_completed' : 'last_feedback_completed_at')"
                           :sortable="true"
                           :sort-method="onSortByTime('customer_feedback_details.last_feedback_completed_at')"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getFormattedFeedbackTime(scope.row, 'customer_feedback_details.last_feedback_completed_at') }} &nbsp;


</template>

<script>
import get from 'lodash/get';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import difference from 'lodash/difference';
import upperFirst from 'lodash/upperFirst';
import dayjs from '@/localPlugins/dayjs';
import { disableFutureDate, alphabetSort } from '@/libs';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PAGINATION_DEFAULT, TAG_LIMIT, TEAM_LIMIT, LOAD_MORE_LIMIT } from '@/constants/pagination';

import ManagementPageWithActions from '@/components/ManagementPage/ManagementPageWithActions.vue';
import LayoutMain from '@/components/layouts/main.vue';
import ActionsPanel from '@/components/ActionsPanel.vue';
import { filterActionsByPermissions } from '../../libs/permissionsHelper';
import { formatCustomerSegmentsForDisplay } from '../../libs/customerSegments';
import { NON_DELETABLE_SEGMENTS } from '@/constants/segment';
import { CHANNELS } from '@/constants';
import { downloadAsFile } from '@/libs';
import { limitedString } from '@/libs/stringUtils';
import TooltipInfo from './components/TooltipInfo.vue';
import CustomerTableControls from './components/CustomerTableControls.vue';

const SEARCH_PAGE_SIZE = 100;

const SEND_SURVEY_ACTION = 'Request feedback';
const UPDATE_PRIMARY_AGENT = 'Update primary agent';
const UPDATE_CUSTOMER_SEGMENTS = 'Update segments';
const SEND_MESSAGE = 'Send message';
const UPDATE_ASSIGNED_AGENTS = 'Update assigned agents';
const CLOSE_CONVERSATION = 'Close conversation';
const SPECIAl_CHAR = /[!#$%^&*()=[\]{};:"\\|,<>?`]+/;
const MAX_ASSIGNED_AGENT_NAME_LENGTH = 20;
const CUSTOMER_TABLE_COLUMN_NAMES = [
  { name: 'name' },
  { name: 'email' },
  { name: 'joined_date' },
  { name: 'last_interaction' },
  { name: 'segment' },
  { name: 'primary_agent' },
  { name: 'assigned_agents' },
  { name: 'last_feedback_sent' },
  { name: 'last_feedback_completed' },
];

const ACTION_OPTIONS = {
  [UPDATE_PRIMARY_AGENT]: {},
  [SEND_MESSAGE]: {},
  [SEND_SURVEY_ACTION]: {},
  [UPDATE_CUSTOMER_SEGMENTS]: {},
  [UPDATE_ASSIGNED_AGENTS]: {},
  [CLOSE_CONVERSATION]: {},
};

export default {
  name: 'customers',
  components: {
    ManagementPageWithActions,
    LayoutMain,
    ActionsPanel,
    TooltipInfo,
    CustomerTableControls,
  },
  data() {
    return {
      actionInProgress: true,
      maxAssignedAgentNameLength: MAX_ASSIGNED_AGENT_NAME_LENGTH,
      chosenAction: null,
      pagination: PAGINATION_DEFAULT,
      params: {},
      isFilterOpen: false,
      actionFormShown: false,
      pageResource: '/customersdashboard',
      searchPageSize: SEARCH_PAGE_SIZE,
      datePickerOptions: {
        'Last Chat Time': 'last_chat_time',
        'Joined Date': 'joined_date',
        'Last Feedback Requested': 'last_feedback_sent_at',
        'Last Feedback Received': 'last_feedback_completed_at',
      },
      disabledDatePickerRange: disableFutureDate,
      customerAgentOptions: {
        'Primary Selected': 'primary_selected',
        'Primary Not Selected': 'primary_not_selected',
        'Has Phone Number': 'has_phone_number',
        'No Messages Sent': 'no_messages_sent',
        Queued: 'queued',
        'Not Queued': 'not_queued',
        Unresponded: 'unresponded',
        'Message Typed': 'typed',
        'Message Not Typed': 'not_typed',
      },

      advancedCustomerAgentOptions: {
        'Messages sent': 'messages_sent',
        'No messages sent': 'no_messages_sent',
        'Phone number provided': 'has_phone_number',
        'Phone number not provided': 'has_no_phone_number',
        'Primary selected': 'primary_selected',
        'Primary not selected': 'primary_not_selected',
        Queued: 'queued',
        'Not queued': 'not_queued',
        'Feedback sent': 'feedback_sent',
        'Feedback completed': 'feedback_returned',
        Unresponded: 'unresponded',
        'Message typed': 'typed',
        'Message not typed': 'not_typed',
      },

      isLoadingAll: false,
      isLoading: false,

      actionOptions: {
        ...ACTION_OPTIONS,
        [UPDATE_PRIMARY_AGENT]: {
          commitActionFunction: this.updatePrimaryAgentFromCustomers,
        },
        [SEND_MESSAGE]: {
          commitActionFunction: this.sendMessageToCustomers,
        },
        [SEND_SURVEY_ACTION]: {
          commitActionFunction: this.sendSurveyToCustomers,
        },
        [UPDATE_CUSTOMER_SEGMENTS]: {
          commitActionFunction: this.updateCustomerSegments,
        },
        [UPDATE_ASSIGNED_AGENTS]: {
          commitActionFunction: this.updateAssignedAgents,
        },
        [CLOSE_CONVERSATION]: {
          commitActionFunction: this.closeConversationToCustomers,
        },
      },
      filterType: '',
      filterByAgent: null,
      advancedDashboardAgentFilter: null,
      defaultSortOrder: { prop: 'last_chat_details.last_chat_time', order: 'ascending' },
    };
  },
  props: {
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['fetchData'],
  watch: {
    filterByAgent() {
      this.$emit('fetchData');
    },
  },
  mounted() {
    if (!this.showAdvancedFilter) {
      this.filterType = this.$route.query['filter-type'];
      this.filterByAgent = this.$route.query['agent-id'] ?
        { id: this.$route.query['agent-id'], fullName: this.$route.query['agent-name'] } :
        null;
    }
    this.$store.dispatch('configs/getPostChatSurvey').then(response => {
      this.localPostChatSurvey = response;
    });
    this.$store.dispatch('agents/getAgentsList', [{
      limit: 999999,
    }]);
    this.$store.dispatch('configs/getCustomerSegments');
    this.$store.dispatch('conversations/getChannelsList');
    this.$store.dispatch('tags/getTagsList', [{ limit: TAG_LIMIT }]);
    this.$store.dispatch('teams/getTeamsList', [{ limit: TEAM_LIMIT }]);
  },
  computed: {
    ...mapState({
      permissions: state => state.agent.permissions,
      agents: state => state.agents.agents,
      isMobileContext: state => state.settings.isMobileContext,
      channelOptions: state => state.conversations.channels,
      profileFields: state => state.configs.config.side_panel.profile_mapping,
      showChannelFilter: state => state.featureFlags.FEATURE_FLAGS.SHOW_CUSTOMER_CHANNEL_FILTER,
      showSmsChannel: s => s.featureFlags.FEATURE_FLAGS.ALLOW_SMS_CHANNEL_CUSTOMER_TAB_ACTIONS,
      showAdvancedFilter: state => state.featureFlags.FEATURE_FLAGS.SHOW_ADVANCED_CUSTOMERS_FILTER,
      columnsConfig: state => get(state, 'configs.config.customers_table.columns', {}),
    }),
    ...mapGetters({
      customersWithoutFullname: 'customersDashboard/customers',
      selectedCustomers: 'customersDashboard/getSelectedCustomers',
      segments: 'configs/segmentNames',
      tagsListState: 'tags/sortedTagNames',
      watchableTeamIds: 'agent/watchableTeamIds',
      teams: 'teams/sortedTeams',
      sortedChannels: 'conversations/sortedChannels',
      sortedAgents: 'agents/nameSortedAgents',
    }),
    customers() {
      return this.customersWithoutFullname
        .map(c => {
          c.fullName = c.fullName === ''
            ? this.$t('customer_tab.table.default_customer_name', { customer_id: c.id })
            : c.fullName;
          return c;
        });
    },
    profileSearchableFields() {
      // Searchable fields are those which can be used in customer query filters. They are profile
      // customizable bc no customizable are include by default in customer query filters
      return [...this.profileFields]
        .filter(field => field.type === 'string' && field.customizable)
        .sort(alphabetSort.bind(null, 'label'));
    },
    aclPermittedActions() {
      return filterActionsByPermissions(this.actionOptions, this.permissions);
    },
    actionCardParams() {
      let params = {
        actionValue: this.chosenAction,
        items: this.selectedCustomers,
        showMessage: true,
      };

      if (this.chosenAction === SEND_SURVEY_ACTION) {
        const optionsValues = (this.localPostChatSurvey && this.localPostChatSurvey.survey_type) ?
          [this.localPostChatSurvey.survey_type.toUpperCase()] : ['CSAT', 'NPS'];
        params = merge(params, {
          showOptions: true,
          showMessage: false,
          requireOption: true,
          optionsValues,
        });
      }

      if (this.chosenAction === UPDATE_PRIMARY_AGENT) {
        params = merge(params, {
          optionsLabelText: 'New Primary Agent:',
          showOptions: true,
          optionsValues: this.agents.map(a => a.fullName),
          allowEmptyOption: true,
          requireOption: false,
          showAttachmentButton: this.canView('/conversations/inputbox/upload'),
        });
      }

      if (this.chosenAction === SEND_MESSAGE) {
        const channels = this.getAvailableChannels(this.selectedCustomers);

        params = merge(params, {
          optionsLabelText: 'Channel:',
          showOptions: true,
          optionsValues: channels,
          allowEmptyOption: false,
          requireOption: true,
          requireMessage: true,
          showAttachmentButton: this.canView('/conversations/inputbox/upload'),
        });
      }

      if (this.chosenAction === UPDATE_CUSTOMER_SEGMENTS) {
        const optionsValues = ['attach', 'detach'];
        const multiOptionsValues = difference(this.segments, NON_DELETABLE_SEGMENTS);
        params = merge(params, {
          optionsLabelText: 'Mode:',
          showOptions: true,
          multiOptionsLabelText: 'Segments:',
          showMultiOptions: true,
          requireMultiOptions: true,
          optionsValues,
          multiOptionsValues,
          showMessage: false,
          requireOption: true,
        });
      }

      if (this.chosenAction === UPDATE_ASSIGNED_AGENTS) {
        const optionsValues = ['attach', 'detach'];
        const multiOptionsValues = this.agents.map(a => a.fullName);
        params = merge(params, {
          optionsLabelText: 'Mode:',
          showOptions: true,
          multiOptionsLabelText: 'Agents:',
          showMultiOptions: true,
          requireMultiOptions: true,
          optionsValues,
          multiOptionsValues,
          showMessage: false,
          requireOption: true,
        });
      }

      if (this.chosenAction === CLOSE_CONVERSATION) {
        params = merge(params, {
          showMessage: false,
        });
      }

      return params;
    },
    preFilterText() {
      if (this.filterByAgent) {
        return `Filter Agent: ${this.filterByAgent.fullName}`;
      }
      return '';
    },
    helpText() { return this.$t('customer_tab.actions.description_tooltip') },
    actionsErrorText() {
      const stringPath = 'customer_tab.actions.error_tooltip.';
      return {
        title: this.$t(`${stringPath}title`),
        description: this.$t(`${stringPath}description`),
      };
    },
    hasRowSelections() {
      return !!Object.keys(this.selectedCustomers).length;
    },
    visibleCustomerTableColumns() {
      if (!this.showAdvancedFilter) {
        return CUSTOMER_TABLE_COLUMN_NAMES;
      }

      return Object.entries(this.columnsConfig)
        .filter(([, columnConfig]) => get(columnConfig, 'show', true))
        .map(([columnName, columnConfig]) => ({
          name: columnName,
          order: get(columnConfig, 'order', Number.MAX_SAFE_INTEGER),
        }))
        .sort((left, right) => left.order - right.order);
    },
    selectedRowsCount() {
      return Object.keys(this.selectedCustomers).length;
    },
  },
  methods: {
    ...mapActions({
      getCustomers: 'customersDashboard/getCustomers',
      updateCustomerSegmentsAction: 'customersDashboard/updateCustomerSegments',
      updateCustomerAssignedAgentsAction: 'customersDashboard/updateCustomerAssigedAgents',
      updatePrimaryAgentFromSelectedCustomers: 'customersDashboard/updatePrimaryAgentFromSelectedCustomers',
      removePrimaryAgentFromSelectedCustomers: 'customersDashboard/removePrimaryAgentFromSelectedCustomers',
      selectCustomers: 'customersDashboard/selectCustomers',
      deselectAllCustomers: 'customersDashboard/deselectAllCustomers',
      sendMessageToSelectedCustomers: 'customersDashboard/sendMessageToSelectedCustomers',
      sendSurveyToSelectedCustomers: 'customersDashboard/sendSurveyToSelectedCustomers',
      closeConversationToSelectedCustomers: 'customersDashboard/closeConversationToSelectedCustomers',
    }),

    getCustomerName(customer) {
      return customer.fullName;
    },

    getCustomerEmail(customer) {
      return get(customer, 'profile.email', '');
    },

    getCustomerPhone(customer) {
      return get(customer, 'profile.phone', '');
    },

    getCustomerExternalId(customer) {
      return get(customer, 'profile.external_id', '');
    },

    getCustomerTableColumnLabel(columnName) {
      return this.$t(`customer_tab.table.columns.${columnName}`);
    },

    getIsCustomerTableColumnVisible(columnName) {
      return get(this.columnsConfig, `${columnName}.show`, true);
    },

    getFormattedCustomerSegment(customer) {
      return formatCustomerSegmentsForDisplay(get(customer, 'customer_segments', []));
    },
    getFormattedJoinedDate(customer) {
      return dayjs(customer.created_at).format('MM/DD/YYYY');
    },


    getFormattedLastChatTime(customer) {
      const lastChatTime = get(customer, 'last_chat_details.last_chat_time');
      if (!lastChatTime) {
        return 'Lynq';
      }
      return dayjs(lastChatTime).format('MM/DD/YYYY hh:mm A');
    },
    getFormattedFeedbackTime(customer, path) {
      const time = get(customer, path);
      if (!time) {
        return '';
      }
      return dayjs(time).format('MM/DD/YYYY hh:mm A');
    },
    goToLastConversation(customer) {
      const lastConversationCustomerId = customer.id;
      if (!lastConversationCustomerId) {
        return;
      }
      this.$router.push({
        name: 'ConversationByCustomerId',
        params: { id: lastConversationCustomerId },
      });
    },

    getPrimaryAgentName(customer) {
      return upperFirst(get(customer, 'primary_agent_details.full_name', ''));
    },

    getAssignedAgentName(agent) {
      return upperFirst(get(agent, 'full_name', ''));
    },
    getTruncatedAssignedAgentName(agent) {
      return limitedString(this.getAssignedAgentName(agent), this.maxAssignedAgentNameLength);
    },
    filterToAgent(agent) {
      const primaryAgentName = get(agent, 'full_name', '');
      if (primaryAgentName) {
        this.$router.push({
          name: 'Users',
          query: {
            query: primaryAgentName,
          },
        });
      }
    },

    closeActionPanel() {
      this.chosenAction = null;
      this.actionFormShown = false;
    },

    onSortName(a, b) {
      return get(a, 'fullName', '').toLowerCase().replace(/\s+/g, '') <= get(b, 'fullName', '').toLowerCase().replace(/\s+/g, '') ? 1 : -1;
    },
    onSortEmail(a, b) {
      return this.getCustomerEmail(a).toLowerCase() <= this.getCustomerEmail(b).toLowerCase() ? 1 : -1;
    },

    onSortPhone(a, b) {
      return this.getCustomerPhone(a).toLowerCase() <= this.getCustomerPhone(b).toLowerCase() ? 1 : -1;
    },

    onSortExternalId(a, b) {
      return this.getCustomerExternalId(a).toLowerCase() <= this.getCustomerExternalId(b).toLowerCase() ? 1 : -1;
    },

    onSortJoinedDate(a, b) {
      return get(a, 'created_at', '') <= get(b, 'created_at', '') ? 1 : -1;
    },

    onSortByTime(path) {
      return (a, b) => {
        const leftTime = get(a, path);
        const rightTime = get(b, path);

        //on downword sort, undefined values should be top
        //on upward sort, undefined should be bottom
        if (!leftTime) {
          return 1;
        }

        if (!rightTime) {
          return -1;
        }

        return leftTime <= rightTime ? 1 : -1;
      };
    },

    onSortPrimaryAgent(a, b) {
      return this.getPrimaryAgentName(a) <= this.getPrimaryAgentName(b) ? 1 : -1;
    },

    onSortCustomerSegment(a, b) {
      return get(a, 'profile.customer_segment', '') <= get(b, 'profile.customer_segment', '') ? 1 : -1;
    },

    onSelect(selection = []) {
      if (selection.length > 0) {
        this.selectCustomers(selection);
      } else {
        this.deselectAllCustomers();
      }
    },

    clearCustomerTableSelection() {
      this.$refs.customersTable?.clearSelection?.();
    },

    clearCustomerSelections() {
      this.deselectAllCustomers();
      this.clearCustomerTableSelection();
    },

    getAvailableChannels(customers) {

      const customersList = isEmpty(customers) ? [] : Object.values(customers);

      let availableChannels = [
        ...new Set(
          customersList.flatMap(customer =>
            isArray(customer.conversation_channels) ? customer.conversation_channels : [],
          ),
        ),
      ].sort((a, b) => String(a).localeCompare(String(b)));

      if (!this.showSmsChannel) {
        availableChannels = availableChannels.filter(channel => channel !== CHANNELS.SMS);
      }

      return availableChannels;
    },

    updateCustomerSegments({ customers, selectedOption, selectedMultiOption }) {
      return this.updateCustomerSegmentsAction({
        mode: selectedOption,
        segments: selectedMultiOption,
        customers,
      });
    },

    async updateAssignedAgents({ customers, selectedOption, selectedMultiOption }) {
      const agents = this.agents
        .filter(a => selectedMultiOption.includes(a.fullName))
        .map(a => a.id);
      return this.updateCustomerAssignedAgentsAction({
        mode: selectedOption,
        agents,
        customers,
      });
    },

    sendMessageToCustomers({ customers, message, selectedOption, files, expirationMessageDate }) {
      return this.sendMessageToSelectedCustomers({
        customers,
        message,
        files,
        channel: selectedOption,
        expirationMessageDate: expirationMessageDate ?
          dayjs(expirationMessageDate).utc().format('YYYY-MM-DD HH:mm:ss.SSSZ') :
          expirationMessageDate,
      });
    },

    sendSurveyToCustomers({ customers, message, selectedOption }) {
      return this.sendSurveyToSelectedCustomers({
        customers,
        message,
        surveyType: selectedOption,
      });
    },

    closeConversationToCustomers({ customers }) {
      return this.closeConversationToSelectedCustomers({ customers });
    },

    chooseAction(action) {
      if (this.actionOptions[action]) {
        this.chosenAction = action;
        this.actionFormShown = true;
      }
    },

    async commitAction({
      actionValue, items, message, selectedOption, selectedMultiOption, files, expirationMessageDate,
    }) {
      const allSelectedCustomers = Object.values(items);

      // Exclude customers that cannot receive the message with the selected channel.
      let customersToCommit = allSelectedCustomers;
      const customersWithoutSelectedChannel = actionValue === SEND_MESSAGE
        ? this.getCustomerWithoutSelectedChannel(allSelectedCustomers, selectedOption)
        : [];

      if (customersWithoutSelectedChannel.length) {
        const list = `<ul>${customersWithoutSelectedChannel.map(item => `<li>${item.id}</li>`).join('')}</ul>`;
        const optionVal = selectedOption === CHANNELS.SMS ? 'SMS' : selectedOption;
        try {
          await this.$aiq.confirm(
            this.$t('customer_dashboard.sending_message.error_title'),
            `${this.$t('customer_dashboard.sending_message.error_dialog', {selectedOption: optionVal})}
            <br><br>
            ${list}`,
            { showCancelButton: false},
          );
        }
        catch {
          // Nothing to handle, just continue since there is no cancel button for this dialog
        }

        customersToCommit = allSelectedCustomers.filter(c => (
          (c.conversation_channels || []).includes(selectedOption)
        ));
      }

      if (customersToCommit.length === 0) {
        this.chosenAction = null;
        this.actionInProgress = false;
        this.clearCustomerSelections();
        await this.loadCustomers(this.params);
        return;
      }

      try {
        this.actionInProgress = true;
        const res = await this.actionOptions[actionValue].commitActionFunction({
          customers: customersToCommit,
          message,
          files,
          selectedOption,
          selectedMultiOption,
          expirationMessageDate,
        });

        if (get(res, 'id')) {
        // the action will be running asynchronously.
        // and the given id is a job id
          this.$aiq.notify.info('The request accepted. You will be notified once the job is completed.');
        } else if (get(res, 'result')) {
        // The number of customers is small and the request is
        // completed
          this.$aiq.notify.success('Successful');
        } else {
          this.$aiq.notify.error('Unexpected response');
        }
      } catch (err) {
        this.$aiq.error.error('Failed');
      }
      finally {
        this.chosenAction = null;
        this.actionInProgress = false;
        this.clearCustomerSelections();
        await this.loadCustomers(this.params);
      }
    },

    getCustomerWithoutSelectedChannel(customers, channel) {
      return customers.filter(c => !(
        (c.conversation_channels || []).includes(channel)
      ));
    },

    removePreFilter() {
      this.filterByAgent = null;
      this.filterType = '';
      const query = Object.assign({}, this.$route.query);
      for (const key of ['agent-id', 'agent-name', 'filter-type']) {
        delete query[key];
      }
      this.$router.replace({ query });
    },
    async loadList(params) {
      const {
        searchTerm,
        searchOffset,
        dateRange,
        datePickerOption,
        customerAgentOption,
        customerChannelOption,
        customerSegmentOption,
        customerTagOption,
        customerTeamOption,
        agentFilterOption,
        primaryAgentFilterOption,
        customerProfileOption,
        periods,
      } = params;


      if (SPECIAl_CHAR.test(searchTerm)) {
        this.$aiq.notify.error(this.$t('metrics_tab.search.notify_errors.no_special_characters'));
        this.$refs.managementPage.updateSearchTerm(searchTerm.replace(SPECIAl_CHAR, ''));
        return;
      }

      this.isLoading = true;
      this.actionInProgress = true;

      this.params = {
        limit: this.searchPageSize,
        query: searchTerm,
        offset: searchOffset,
        date_type: this.datePickerOptions[datePickerOption],
      };

      this.$refs.customersTable.sort(this.params.date_type, 'ascending');

      if (dateRange) {
        if (dateRange[0]) {
          this.params.period_start = dayjs(dateRange[0]).unix();
        }
        if (dateRange[1]) {
          this.params.period_end = dayjs(dateRange[1]).unix();
        }
      }

      if (this.showAdvancedFilter && this.advancedDashboardAgentFilter) {
        this.params.filter_agent_id = this.advancedDashboardAgentFilter.agentId;
        this.params.filter_type = this.advancedDashboardAgentFilter.filterType;
      } else if (!this.showAdvancedFilter && this.filterByAgent) {
        this.params.filter_agent_id = this.filterByAgent.id;
        this.params.filter_type = this.filterType;
      }

      if (Array.isArray(customerAgentOption) && customerAgentOption.length) {
        this.params.attributes = customerAgentOption.map(option => this.advancedCustomerAgentOptions[option]).filter(Boolean);
      } else if (customerAgentOption) {
        this.params.attribute = customerAgentOption in this.customerAgentOptions
          ? this.customerAgentOptions[customerAgentOption]
          : null;
      }

      if (customerChannelOption) {
        this.params.channel = customerChannelOption;
      }

      if (customerSegmentOption) {
        this.params.segments = customerSegmentOption;
      }

      if (customerProfileOption) {
        this.params.profile_field = customerProfileOption.field;
      }

      if (Array.isArray(customerTagOption) && customerTagOption.length) {
        this.params.tags = customerTagOption;
      }

      if (Array.isArray(customerTeamOption) && customerTeamOption.length) {
        this.params.team_ids = customerTeamOption;
      }

      if (Array.isArray(agentFilterOption) && agentFilterOption.length) {
        this.params.agent_ids = agentFilterOption;
      }

      if (Array.isArray(primaryAgentFilterOption) && primaryAgentFilterOption.length) {
        this.params.primary_agent_ids = primaryAgentFilterOption;
      }

      if (Array.isArray(periods) && periods.length) {
        const formattedPeriods = periods
          .map(period => ({
            date_type: period.date_type,
            ...(period.period_start && { period_start: dayjs(period.period_start).unix() }),
            ...(period.period_end && { period_end: dayjs(period.period_end).unix() }),
          }))
          .filter(period => period.date_type && (period.period_start || period.period_end));

        if (formattedPeriods.length) {
          this.params.periods = JSON.stringify(formattedPeriods);
        }
      }

      if (this.watchableTeamIds.length) {
        this.params.teams = this.watchableTeamIds;
      }
      return this.loadCustomers(this.params);
    },

    applyAdvancedFilter(filters) {
      this.clearCustomerTableSelection();
      this.advancedDashboardAgentFilter = filters.dashboardAgentFilter || null;
      const managementPage = this.$refs.managementPage;

      if (managementPage) {
        managementPage.applyFetchState(filters);
        managementPage.fetchData();
        return;
      }

      this.loadList(filters);
    },

    async loadSome(customersCount) {
      this.isLoadingAll = true;
      let hasMoreVisibleResults = true;

      while (hasMoreVisibleResults && this.isLoadingAll && !this.isAllCustomersPageLoaded() && customersCount > this.searchPageSize) {
        const previousVisibleResults = this.getVisibleCustomerResults();
        this.params.offset = this.pagination.offset + this.searchPageSize;
        await this.loadCustomers(this.params); // eslint-disable-line no-await-in-loop
        customersCount -= this.searchPageSize;
        hasMoreVisibleResults = this.getVisibleCustomerResults() > previousVisibleResults;
      }
      this.syncManagementPageOffset();
      this.stopLoadAll();
    },

    async loadAll() {
      this.isLoadingAll = true;

      while (this.isLoadingAll && !this.isAllCustomersPageLoaded()) {
        const previousVisibleResults = this.getVisibleCustomerResults();
        this.params.offset = this.pagination.offset + this.searchPageSize;
        await this.loadCustomers(this.params); // eslint-disable-line no-await-in-loop

        if (this.getVisibleCustomerResults() <= previousVisibleResults) {
          break;
        }
      }
      this.syncManagementPageOffset();
      this.stopLoadAll();
    },

    async stopLoadAll() {
      this.isLoadingAll = false;
      this.isLoading = false;
    },

    getVisibleCustomerResults() {
      return Math.min(
        this.pagination.offset + this.pagination.limit,
        this.pagination.rowCount,
      );
    },

    syncManagementPageOffset() {
      if (this.$refs.managementPage) {
        this.$refs.managementPage.offset = this.getVisibleCustomerResults();
      }
    },

    isAllCustomersPageLoaded() {
      return this.pagination.rowCount <= this.getVisibleCustomerResults();
    },

    toggleOpen(isOpen) {
      this.isFilterOpen = isOpen;
    },

    downloadData() {
      downloadAsFile({
        dataType: 'table',
        data: this.customers.map(customer => ({
          fullname: this.getCustomerName(customer),
          email: this.getCustomerEmail(customer),
          joined_date: this.getFormattedJoinedDate(customer),
          last_chat_time: this.getFormattedLastChatTime(customer),
          customer_segment: this.getFormattedCustomerSegment(customer),
          primary_agent: this.getPrimaryAgentName(customer),
        })),
      });
    },

    async loadCustomers(params, refresh = true) {
      this.isLoading = true;
      try {
        const { customers, pagination } = await this.getCustomers({
          params,
          update: refresh && (get(params, 'offset') === 0),
        });
        this.pagination = pagination;
        return customers;
      } catch (err) {
        this.$aiq.notify.error(this.$t('customer_dashboard.loading_failed'));

        this.pagination = PAGINATION_DEFAULT;
        return [];

      } finally {
        this.actionInProgress = false;
        this.isLoading = false;
      }
    },

    // Deprecated
    async removePrimaryAgentFromCustomers({ customers, message }) {
      await this.removePrimaryAgentFromSelectedCustomers({
        customers,
        message,
      });
      this.deselectAllCustomers();
    },

    updatePrimaryAgentFromCustomers({ customers, message, selectedOption, files }) {
      if (!selectedOption) {
        return this.updatePrimaryAgentFromSelectedCustomers({
          mode: 'remove',
          customers,
          message,
          files,
        });
      }

      const agent = this.agents.find(a => a.fullName === selectedOption);
      return this.updatePrimaryAgentFromSelectedCustomers({
        mode: 'replace',
        customers,
        message,
        files,
        agent,
      });
    },

    async loadAndSelectSome() {
      if (this.customers.length < LOAD_MORE_LIMIT) {
        await this.loadSome(LOAD_MORE_LIMIT);
      }

      await this.$nextTick();
      this.$refs.customersTable.clearSelection();
      this.customers.forEach(customer => {
        this.$refs.customersTable.toggleRowSelection(customer, true);
      });
    },
  },
  beforeUnmount() {
    this.stopLoadAll();
  },
};

</script>

<style lang="scss" scoped>
@import "../../styles/aiq-mixins.scss";
@import "../../styles/media-queries.scss";

  .el-table--fit{
    height: calc(100% - 115px);
  }

  .status-indicator {
    &::before {
      margin-top: 1px;
    }
  }

  .agent-button {
    white-space: break-spaces;
    text-align: left;
    word-break: break-word;
    margin-left: 3px;
    padding-top: 3px;
    padding-bottom: 3px;
  }

  :deep(.aiq-table-totals) {
    .el-tag {
      .el-tag__close {
        color:#409eff;

        &:hover {
          color:white;
        }
      }
    }
  }

</style>

<style lang="scss">
@import "../../styles/aiq-mixins.scss";
@import "../../styles/media-queries.scss";

.customers-scrollbar {

  .el-table__header {
    .cell {
      word-break: normal;
      text-align: center;
    }
  }

  .el-table__body-wrapper {
    @include scrollable(calc(100vh - 247px));

    .date-button span{
      white-space: normal;
      word-break: break-all;
    }
  }
}
.add-edit-actions-panel {
  .el-dialog {
      @include mobile {
        min-width: 90%;
    }
  }
}

.date-range-filter.filter-in-header {
  .date-range-filter-date-field.filter-in-header {
    .el-date-editor {
      .el-input__wrapper {
        height: 30px !important;
      }
    }
  }
}

</style>
